import {
  getHostedIdentifySessionRequirementsOptions,
  postHostedIdentifySessionOptions,
  postHostedIdentifySessionVerifyOptions,
} from '@onefootprint/fetch-hosted';
import type {
  IdentifyRequirementChallenge,
  IdentifyRequirementCollectData,
  IdentifyRequirementLogin,
} from '@onefootprint/request-types';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { retryOnConnectionError } from '@/idv2/components/errors/get-error-key';
import { runSnaChallenge } from '@/idv2/features/identify/challenge-sna';
import { getIdentifySessionRequest } from '@/idv2/features/identify/get-identify-session-request';
import { clearIdentifyBack } from '@/idv2/features/identify/identify-back';
import { getValidatedIdentifyBootstrap } from '@/idv2/features/identify/validate-bootstrap';
import { setOnboardingSessionToken } from '@/idv2/features/idv-to-idv2';
import { checkDeviceInfo } from '@/idv2/utils/get-device';
import { redirectAfterIdentify, reportIdentifyDone } from '@/idv2/utils/loader-navigation';
import { getLogger } from '@/idv2/utils/logger';

const { logInfo } = getLogger({ name: 'identify-index' });

const identifyIndexSearchSchema = z.object({
  email: z.string().optional(),
});

const pickOtpChallengeKind = (kinds: string[]): 'sms' | 'email' | undefined => {
  if (kinds.includes('sms')) return 'sms';
  if (kinds.includes('email')) return 'email';
  return undefined;
};

type LoaderContext = {
  resolvedEmail?: string;
  bootstrapPhone?: string;
  isEditingLoginMethods?: boolean;
};

const handleCollectData = (requirement: IdentifyRequirementCollectData, ctx: LoaderContext): never => {
  const { cdo } = requirement;
  if (cdo === 'email') {
    throw redirect({ to: '/identify/email', search: { email: ctx.resolvedEmail } });
  }
  if (cdo === 'phone_number') {
    throw redirect({ to: '/identify/phone', search: { email: ctx.resolvedEmail, phone: ctx.bootstrapPhone } });
  }
  throw new Error(`Unknown collect_data CDO: ${cdo}`, { cause: requirement });
};

const handleLogin = async (requirement: IdentifyRequirementLogin, ctx: LoaderContext): Promise<never> => {
  const kinds = requirement.user?.availableChallengeKinds ?? [];
  const otpMethods = kinds.filter(k => k === 'sms' || k === 'email');
  const hasMultipleOtpMethods = otpMethods.length > 1;

  let canShowBiometric = false;
  if (kinds.includes('biometric')) {
    const device = await checkDeviceInfo();
    canShowBiometric =
      device.hasSupportForWebauthn && (device.type === 'mobile' || !!requirement.user?.hasSyncablePasskey);
  }
  const isOnlyBiometric = canShowBiometric && otpMethods.length === 0;

  // Members who lost their phone get in through "Send code to email", which only the options screen offers.
  if (ctx.isEditingLoginMethods || hasMultipleOtpMethods || canShowBiometric || isOnlyBiometric) {
    throw redirect({ to: '/identify/login' });
  }

  const otpMethod = pickOtpChallengeKind(kinds);
  if (otpMethod) {
    throw redirect({
      to: '/identify/otp/$challengeKind',
      params: { challengeKind: otpMethod },
      search: {
        email: ctx.resolvedEmail,
        scrubbedEmail: requirement.user?.scrubbedEmail,
        scrubbedPhone: requirement.user?.scrubbedPhone,
      },
    });
  }
  throw redirect({ to: '/identify/login' });
};

const handleChallenge = async (
  requirement: IdentifyRequirementChallenge,
  ctx: LoaderContext & { onboardingSessionToken: string },
): Promise<never> => {
  const challengeKinds = requirement.challengeKinds ?? [];
  const otpFallback = pickOtpChallengeKind(challengeKinds);

  if (challengeKinds.includes('sna') && otpFallback) {
    let snaSucceeded = false;
    try {
      await runSnaChallenge(ctx.onboardingSessionToken);
      snaSucceeded = true;
    } catch (error) {
      logInfo('SNA challenge failed, falling back to OTP', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
    if (snaSucceeded) {
      clearIdentifyBack();
      throw redirect({ to: '/identify' });
    }
  }

  if (otpFallback) {
    throw redirect({
      to: '/identify/otp/$challengeKind',
      params: { challengeKind: otpFallback },
      search: {
        email: ctx.resolvedEmail,
        phone: ctx.bootstrapPhone,
      },
    });
  }

  throw new Error('Unknown challenge requirement', { cause: requirement });
};

export const Route = createFileRoute('/identify/')({
  validateSearch: identifyIndexSearchSchema,
  beforeLoad: async ({ context, search }) => {
    const token = context.onboardingSessionToken;

    // Create identify session when there is no token yet (unified flow from idv)
    if (!token && (context.initialAuthToken || context.obConfigAuth)) {
      const sessionData = await context.queryClient.fetchQuery({
        ...postHostedIdentifySessionOptions(getIdentifySessionRequest(context)),
        retry: retryOnConnectionError,
      });
      setOnboardingSessionToken(sessionData.token);
      throw redirect({ to: '/identify' });
    }

    const data = await context.queryClient.fetchQuery(
      getHostedIdentifySessionRequirementsOptions({
        headers: { 'X-Fp-Authorization': token },
      }),
    );

    if (data?.requirements?.length === 0) {
      const verifyData = await context.queryClient.ensureQueryData({
        ...postHostedIdentifySessionVerifyOptions({
          headers: { 'X-Fp-Authorization': token },
        }),
        retry: retryOnConnectionError,
      });

      setOnboardingSessionToken(verifyData.authToken);

      // Editing login methods needs only the verified token, so it skips onboarding.
      if (context.afterIdentify === '/auth-methods') {
        reportIdentifyDone(context.onIdentifyDone, { authToken: verifyData.authToken });
        throw redirect({ to: '/auth-methods' });
      }

      if (context.afterIdentify === 'host') {
        reportIdentifyDone(context.onIdentifyDone, { authToken: verifyData.authToken });
        // Starting onboarding here too would POST /hosted/onboarding twice, and the host's call
        // fails. Stay on the pending shimmer until the host unmounts idv2.
        return new Promise<never>(() => {});
      }

      throw await redirectAfterIdentify(context, { authToken: verifyData.authToken });
    }

    // Return the identify-specific context plus search and bootstrap for the loader to thread
    const bootstrap = getValidatedIdentifyBootstrap(context.kycBootstrapData);
    return {
      onboardingSessionToken: token,
      currentRequirement: data.requirements[0],
      email: search.email,
      bootstrapEmail: bootstrap.bootstrapEmail,
      bootstrapPhone: bootstrap.bootstrapPhone,
    };
  },
  loader: async ({
    context: { onboardingSessionToken, currentRequirement, email, bootstrapEmail, bootstrapPhone, afterIdentify },
  }) => {
    const ctx = {
      resolvedEmail: email ?? bootstrapEmail,
      bootstrapPhone,
      onboardingSessionToken,
      isEditingLoginMethods: afterIdentify === '/auth-methods',
    };

    switch (currentRequirement.kind) {
      case 'collect_data':
        return handleCollectData(currentRequirement, ctx);
      case 'login':
        return handleLogin(currentRequirement, ctx);
      case 'challenge':
        return handleChallenge(currentRequirement, ctx);
      default:
        throw new Error('Unknown identify requirement', { cause: currentRequirement });
    }
  },
});
